import { supabase, TABLES } from './supabase';
import { Match, Pairing, MatchResult } from '../types/match';
import { Player } from '../types/player';

export class MatchService {
  // 开始比赛（保存为进行中状态）
  static async startMatch(
    pairing: Pairing,
    queueSnapshot: Player[]
  ): Promise<Match> {
    const { data, error } = await supabase
      .from(TABLES.MATCHES)
      .insert({
        team1_player1_id: pairing.team1[0].id,
        team1_player2_id: pairing.team1[1].id,
        team2_player1_id: pairing.team2[0].id,
        team2_player2_id: pairing.team2[1].id,
        status: 'in_progress',
        result: null,
        queue_snapshot: queueSnapshot.map(p => ({
          id: p.id,
          name: p.name,
          number: p.number,
        })),
      })
      .select(`
        *,
        team1_player1:team1_player1_id(id, name, number),
        team1_player2:team1_player2_id(id, name, number),
        team2_player1:team2_player1_id(id, name, number),
        team2_player2:team2_player2_id(id, name, number)
      `)
      .single();

    if (error) {
      throw new Error(`开始比赛失败: ${error.message}`);
    }

    return this.mapMatchData(data);
  }

  // 完成比赛（更新结果）
  static async completeMatch(
    matchId: string,
    result: MatchResult
  ): Promise<Match> {
    const { data, error } = await supabase
      .from(TABLES.MATCHES)
      .update({
        status: 'completed',
        result,
      })
      .eq('id', matchId)
      .select(`
        *,
        team1_player1:team1_player1_id(id, name, number),
        team1_player2:team1_player2_id(id, name, number),
        team2_player1:team2_player1_id(id, name, number),
        team2_player2:team2_player2_id(id, name, number)
      `)
      .single();

    if (error) {
      throw new Error(`完成比赛失败: ${error.message}`);
    }

    return this.mapMatchData(data);
  }

  // 获取进行中的比赛
  static async getInProgressMatch(): Promise<Match | null> {
    const { data, error } = await supabase
      .from(TABLES.MATCHES)
      .select(`
        *,
        team1_player1:team1_player1_id(id, name, number),
        team1_player2:team1_player2_id(id, name, number),
        team2_player1:team2_player1_id(id, name, number),
        team2_player2:team2_player2_id(id, name, number)
      `)
      .eq('status', 'in_progress')
      .order('played_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) {
      throw new Error(`获取进行中的比赛失败: ${error.message}`);
    }

    return data ? this.mapMatchData(data) : null;
  }

  // 保存比赛结果（旧方法，保持兼容性）
  static async saveMatch(
    pairing: Pairing,
    result: MatchResult,
    queueSnapshot: Player[]
  ): Promise<Match> {
    const { data, error } = await supabase
      .from(TABLES.MATCHES)
      .insert({
        team1_player1_id: pairing.team1[0].id,
        team1_player2_id: pairing.team1[1].id,
        team2_player1_id: pairing.team2[0].id,
        team2_player2_id: pairing.team2[1].id,
        status: 'completed',
        result,
        queue_snapshot: queueSnapshot.map(p => ({
          id: p.id,
          name: p.name,
          number: p.number,
        })),
      })
      .select(`
        *,
        team1_player1:team1_player1_id(id, name, number),
        team1_player2:team1_player2_id(id, name, number),
        team2_player1:team2_player1_id(id, name, number),
        team2_player2:team2_player2_id(id, name, number)
      `)
      .single();

    if (error) {
      throw new Error(`保存比赛结果失败: ${error.message}`);
    }

    return this.mapMatchData(data);
  }

  // 映射数据库数据到 Match 对象
  private static mapMatchData(data: any): Match {
    return {
      id: data.id,
      pairing: {
        team1: [
          {
            id: data.team1_player1.id,
            name: data.team1_player1.name,
            number: data.team1_player1.number,
            createdAt: new Date(),
            isActive: true,
          },
          {
            id: data.team1_player2.id,
            name: data.team1_player2.name,
            number: data.team1_player2.number,
            createdAt: new Date(),
            isActive: true,
          },
        ] as [Player, Player],
        team2: [
          {
            id: data.team2_player1.id,
            name: data.team2_player1.name,
            number: data.team2_player1.number,
            createdAt: new Date(),
            isActive: true,
          },
          {
            id: data.team2_player2.id,
            name: data.team2_player2.name,
            number: data.team2_player2.number,
            createdAt: new Date(),
            isActive: true,
          },
        ] as [Player, Player],
        createdAt: new Date(data.played_at),
      },
      status: data.status,
      result: data.result as MatchResult | undefined,
      playedAt: new Date(data.played_at),
      queueSnapshot: data.queue_snapshot as Player[],
    };
  }

  // 获取比赛历史（只返回已完成的比赛）
  static async getMatchHistory(limit: number = 50, offset: number = 0): Promise<Match[]> {
    const { data, error } = await supabase
      .from(TABLES.MATCHES)
      .select(`
        *,
        team1_player1:team1_player1_id(id, name, number),
        team1_player2:team1_player2_id(id, name, number),
        team2_player1:team2_player1_id(id, name, number),
        team2_player2:team2_player2_id(id, name, number)
      `)
      .eq('status', 'completed')
      .order('played_at', { ascending: false })
      .range(offset, offset + limit - 1);

    if (error) {
      throw new Error(`获取比赛历史失败: ${error.message}`);
    }

    return data.map(match => this.mapMatchData(match));
  }

  // 获取玩家统计信息
  static async getPlayerStatistics(): Promise<any[]> {
    const { data, error } = await supabase
      .from(TABLES.PLAYER_STATISTICS)
      .select('*')
      .order('number');

    if (error) {
      throw new Error(`获取统计信息失败: ${error.message}`);
    }

    return data;
  }

  // 获取特定玩家的比赛记录
  static async getPlayerMatches(playerId: string, limit: number = 20): Promise<Match[]> {
    const { data, error } = await supabase
      .from(TABLES.MATCHES)
      .select(`
        *,
        team1_player1:team1_player1_id(id, name, number),
        team1_player2:team1_player2_id(id, name, number),
        team2_player1:team2_player1_id(id, name, number),
        team2_player2:team2_player2_id(id, name, number)
      `)
      .or(`team1_player1_id.eq.${playerId},team1_player2_id.eq.${playerId},team2_player1_id.eq.${playerId},team2_player2_id.eq.${playerId}`)
      .eq('status', 'completed')
      .order('played_at', { ascending: false })
      .limit(limit);

    if (error) {
      throw new Error(`获取玩家比赛记录失败: ${error.message}`);
    }

    return data.map(match => this.mapMatchData(match));
  }
}